import React from "react";
import PropTypes from "prop-types";
import { stringToColor } from "../../../components/CalendarCalendar/calendarHelpers";
import { Button } from "../../../components/ui";
import styles from "../Waitlist.module.css";

const TIME_ZONE = "America/Sao_Paulo";

/**
 * Iniciais (até 2) a partir do nome do cliente, para o avatar.
 * @param {string} name
 * @returns {string}
 */
function getInitials(name) {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Formata um instante (ISO/Date) como "dd/MM às HHh" em BRT.
 * @param {string|Date} value
 * @returns {string}
 */
function formatWindowPart(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("pt-BR", {
    timeZone: TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
  });
  const time = d.toLocaleTimeString("pt-BR", {
    timeZone: TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} às ${time}`;
}

/**
 * Diferença em dias inteiros entre `to` e `from` (to - from), arredondada.
 * Positiva quando `to` é posterior a `from`.
 * @param {Date} from
 * @param {Date} to
 * @returns {number}
 */
function diffInDays(from, to) {
  const ms = to.getTime() - from.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

/**
 * Frase relativa de quando o cliente entrou na fila.
 * @param {string|Date} createdAt
 * @returns {string}
 */
function joinedAgoLabel(createdAt) {
  if (!createdAt) return "";
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return "";
  const days = diffInDays(created, new Date());
  if (days <= 0) return "Entrou na fila hoje";
  if (days === 1) return "Entrou na fila há 1 dia";
  return `Entrou na fila há ${days} dias`;
}

/**
 * Frase relativa de expiração + flag de alerta (< 7 dias).
 * @param {string|Date} expiresAt
 * @returns {{ label: string, soon: boolean }|null}
 */
function expiresInLabel(expiresAt) {
  if (!expiresAt) return null;
  const expires = new Date(expiresAt);
  if (Number.isNaN(expires.getTime())) return null;
  const days = diffInDays(new Date(), expires);
  if (days < 0) return { label: "Expirada", soon: true };
  if (days === 0) return { label: "Expira hoje", soon: true };
  if (days === 1) return { label: "Expira em 1 dia", soon: true };
  return { label: `Expira em ${days} dias`, soon: days < 7 };
}

const STATUS_BADGE = {
  ACTIVE: { label: "⏳ Aguardando", className: "statusActive" },
  NOTIFIED: { label: "📩 Notificado", className: "statusNotified" },
};

/**
 * Item individual da lista de espera.
 *
 * @param {{ entry: object, onRemove: (id: string, clientName: string) => void, removing: boolean }} props
 */
export default function WaitlistItem({ entry, onRemove, removing }) {
  const {
    id,
    clientName,
    clientPhone,
    serviceId,
    service,
    windowStart,
    windowEnd,
    createdAt,
    expiresAt,
    notes,
    status,
  } = entry;

  const serviceName = serviceId && service?.name ? service.name : "Qualquer serviço";
  const badge = STATUS_BADGE[status];
  const expiry = expiresInLabel(expiresAt);
  const canRemove = status === "ACTIVE" || status === "NOTIFIED";

  return (
    <li className={styles.item}>
      <div className={styles.itemTop}>
        <span
          className={styles.avatar}
          style={{ background: stringToColor(clientName || id) }}
          aria-hidden="true"
        >
          {getInitials(clientName)}
        </span>

        <div className={styles.itemHead}>
          <span className={styles.clientName}>{clientName || "Cliente"}</span>
          {badge && (
            <span
              className={`${styles.statusBadge} ${styles[badge.className]}`}
              role="status"
            >
              {badge.label}
            </span>
          )}
        </div>
      </div>

      <div className={styles.itemBody}>
        {clientPhone && (
          <p className={styles.metaLine}>
            <span aria-hidden="true">📱</span> {clientPhone}
          </p>
        )}
        <p className={styles.metaLine}>
          <span aria-hidden="true">✂️</span> {serviceName}
        </p>
        <p className={styles.metaLine}>
          <span aria-hidden="true">🗓️</span> Janela: {formatWindowPart(windowStart)} →{" "}
          {formatWindowPart(windowEnd)}
        </p>
        <p className={styles.metaSub}>
          {joinedAgoLabel(createdAt)}
          {expiry && (
            <>
              {" · "}
              <span className={expiry.soon ? styles.expirySoon : undefined}>
                {expiry.label}
              </span>
            </>
          )}
        </p>

        {notes && notes.trim() && (
          <p className={styles.notes}>
            <span aria-hidden="true">📝</span> <em>&ldquo;{notes.trim()}&rdquo;</em>
          </p>
        )}
      </div>

      {canRemove && (
        <div className={styles.itemActions}>
          <Button
            variant="danger"
            onClick={() => onRemove(id, clientName)}
            isLoading={removing}
            disabled={removing}
          >
            Remover da fila
          </Button>
        </div>
      )}
    </li>
  );
}

WaitlistItem.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string.isRequired,
    clientName: PropTypes.string,
    clientPhone: PropTypes.string,
    serviceId: PropTypes.string,
    service: PropTypes.shape({ name: PropTypes.string }),
    windowStart: PropTypes.string,
    windowEnd: PropTypes.string,
    createdAt: PropTypes.string,
    expiresAt: PropTypes.string,
    notes: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  removing: PropTypes.bool,
};

WaitlistItem.defaultProps = {
  removing: false,
};
